import { FC, useState, type MouseEvent } from 'react';
import { Alert, Box, Snackbar, Typography } from '@mui/material';

import styles from './IdeaCard.module.scss';
import { DateIcon, DeleteIcon, EditIcon, RecentlyEditedIcon, TripIcon } from '../../../../../components/Icons';
import { IdeaStopListItem } from '../../../../../types/idea.types';
import { formatDateWithYear } from '../../../../../utils/dateUtils';
import ThreeDotsMenu, { MenuItemConfig } from '../../../../../components/ThreeDotsMenu/ThreeDotsMenu';
import CoverUploadModal from '../../../../../common/CoverUploadModal/CoverUploadModal';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';
import {
  useDeleteIdeaStopCustomCoverMutation,
  useUploadIdeaStopCustomCoverMutation,
} from '../../../../../store/services/ideasApi';

import ideaStopCoverFallback from '../../../../../assets/images/TripMockDetails.jpg';

import SendIdeaStopToTripModal from '../SendIdeaStopToTripModal/SendIdeaStopToTripModal';

type PendingConfirm = null | 'cover';

interface IdeaCardProps {
  ideaStop: IdeaStopListItem;
  ideaId: number;
  isSelected?: boolean;
  onSelect?: () => void;
  onEdit?: (ideaStop: IdeaStopListItem) => void;
  onDeleteRequest?: (ideaStop: IdeaStopListItem) => void;
  onCoverUpdated?: () => void;
  onSentToTrip?: () => void;
  onChildModalOpen?: () => void;
}

const stopMenuEvent = (event: MouseEvent) => {
  event.stopPropagation();
  event.preventDefault();
};

const IdeaCard: FC<IdeaCardProps> = ({
  ideaStop,
  ideaId,
  isSelected = false,
  onSelect,
  onEdit,
  onDeleteRequest,
  onCoverUpdated,
  onSentToTrip,
  onChildModalOpen,
}) => {
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const [sendToTripOpen, setSendToTripOpen] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadStopCover] = useUploadIdeaStopCustomCoverMutation();
  const [deleteStopCover, { isLoading: isDeletingCover }] = useDeleteIdeaStopCustomCoverMutation();
  const coverUrl = ideaStop.custom_cover;

  const handleCardClick = () => {
    onSelect?.();
  };

  const performCoverDelete = async () => {
    try {
      await deleteStopCover({ ideaStopId: ideaStop.id, ideaId }).unwrap();
      setPendingConfirm(null);
      onCoverUpdated?.();
    } catch (error) {
      console.error('Failed to delete idea stop cover:', error);
    }
  };

  const menuItems: MenuItemConfig[] = [
    {
      id: 'idea-stop-cover-upload',
      label: coverUrl ? 'Change cover' : 'Upload cover',
      icon: <EditIcon />,
      disabled: isDeletingCover,
      onClick: event => {
        stopMenuEvent(event);
        onChildModalOpen?.();
        setShowCoverModal(true);
      },
    },
    ...(coverUrl
      ? [
          {
            id: 'idea-stop-cover-delete',
            label: 'Delete cover',
            icon: <DeleteIcon />,
            variant: 'danger' as const,
            disabled: isDeletingCover,
            onClick: (event: MouseEvent) => {
              stopMenuEvent(event);
              onChildModalOpen?.();
              setPendingConfirm('cover');
            },
          },
        ]
      : []),
    {
      id: 'send-stop-to-trip',
      label: 'Send to Trip',
      icon: <TripIcon size={24} />,
      onClick: (event: MouseEvent) => {
        stopMenuEvent(event);
        onChildModalOpen?.();
        setSendToTripOpen(true);
      },
    },
    {
      id: 'edit',
      label: 'Edit Idea Stop',
      icon: <RecentlyEditedIcon />,
      onClick: event => {
        stopMenuEvent(event);
        onEdit?.(ideaStop);
      },
    },
    {
      id: 'delete',
      label: 'Delete Idea Stop',
      icon: <DeleteIcon />,
      variant: 'danger',
      onClick: event => {
        stopMenuEvent(event);
        onDeleteRequest?.(ideaStop);
      },
    },
  ];

  return (
    <>
      <Box
        className={`${styles.ideaCard} ${isSelected ? styles.ideaCardSelected : ''}`}
        onClick={handleCardClick}
      >
        <Box className={styles.ideaImageContainer}>
          <Box
            className={styles.ideaImage}
            sx={{
              backgroundImage: `url(${coverUrl || ideaStopCoverFallback})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <Box
              className={styles.menuWrapper}
              onClick={stopMenuEvent}
              onMouseDown={stopMenuEvent}
            >
              <ThreeDotsMenu
                menuItems={menuItems}
                iconSize={28}
                iconClassName={styles.menuIconOnImage}
              />
            </Box>
            <Box className={styles.typeBadge}>
              <Typography variant="h12Semibold">{ideaStop.stop_type}</Typography>
            </Box>
          </Box>
        </Box>
        <Box className={styles.ideaContent}>
          <Typography
            variant="h10Semibold"
            className={styles.ideaTitle}
          >
            {ideaStop.title || 'Idea Stop'}
          </Typography>
          {ideaStop.description && (
            <Typography
              variant="h11"
              className={styles.ideaDescription}
            >
              {ideaStop.description}
            </Typography>
          )}
          <Box className={styles.ideaDate}>
            <DateIcon size={20} />
            <Typography variant="h11">
              {ideaStop.start_date_time ? formatDateWithYear(ideaStop.start_date_time) : '—'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <CoverUploadModal
        open={showCoverModal}
        onClose={() => setShowCoverModal(false)}
        title="Update Idea Stop Cover"
        description="Choose a new image for this stop cover."
        placeholder={{
          title: 'Select your stop cover',
          subtitle: '(e.g., PNG, JPG, WEBP up to 5MB)',
        }}
        uploadFile={file =>
          uploadStopCover({ ideaStopId: ideaStop.id, file, ideaId })
            .unwrap()
            .then(() => {
              onCoverUpdated?.();
            })
        }
      />

      <ConfirmActionModal
        open={pendingConfirm !== null}
        onClose={() => setPendingConfirm(null)}
        onConfirm={performCoverDelete}
        description="Do you really want to delete the cover image?"
        confirmLabel="Delete"
        isConfirming={isDeletingCover}
      />

      <SendIdeaStopToTripModal
        open={sendToTripOpen}
        onClose={() => setSendToTripOpen(false)}
        ideaStopId={ideaStop.id}
        onSuccess={tripName => {
          setSuccessMessage(`Stop added to '${tripName}'.`);
          setSuccessSnackbarOpen(true);
          onSentToTrip?.();
        }}
      />

      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default IdeaCard;
